import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ShieldCheck, ShieldAlert, Loader2, Cloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { system: "", metric: "", threshold: "", enabled: true, name: "", alert_channel: "in_app" };

const CHANNELS = [
  { id: "in_app", label: "In-app feed (Overview)" },
];
const CHANNEL_LABEL = { in_app: "In-app", email: "Email", slack: "Slack" };

export default function GovernanceRules() {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const canWrite = !restricted && user?.can_mutate !== false;
  const [meta, setMeta] = useState({ systems: [], metrics: [] });
  const [connected, setConnected] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rule id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const metricUnit = meta.metrics.find((m) => m.id === form.metric)?.unit;

  const load = async () => {
    try {
      const [m, r, c] = await Promise.all([api.get("/meta"), api.get("/governance/rules"), api.get("/connections")]);
      setMeta(m.data);
      setRules(r.data);
      setConnected((c.data || []).filter((x) => x.connected).map((x) => x.system));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule.id);
    setForm({
      system: rule.system,
      metric: rule.metric,
      threshold: String(rule.threshold),
      enabled: rule.enabled,
      name: rule.name || "",
      alert_channel: rule.alert_channel || "in_app",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.system || !form.metric || form.threshold === "") {
      toast.error("Please fill in all fields");
      return;
    }
    setSaving(true);
    const body = {
      system: form.system,
      metric: form.metric,
      threshold: Number(form.threshold),
      enabled: form.enabled,
      name: form.name.trim(),
      alert_channel: form.alert_channel,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/governance/rules/${editing}`, body);
        setRules((rs) => rs.map((r) => (r.id === editing ? data : r)));
        toast.success("Rule updated");
      } else {
        const { data } = await api.post("/governance/rules", body);
        setRules((rs) => [data, ...rs]);
        toast.success("Rule created");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      const { data } = await api.put(`/governance/rules/${rule.id}`, {
        system: rule.system,
        metric: rule.metric,
        threshold: rule.threshold,
        enabled: !rule.enabled,
        name: rule.name || "",
        alert_channel: rule.alert_channel || "in_app",
      });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? data : r)));
    } catch (e) {
      toast.error("Failed to update rule");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/governance/rules/${deleteId}`);
      setRules((rs) => rs.filter((r) => r.id !== deleteId));
      toast.success("Rule deleted");
    } catch (e) {
      toast.error("Failed to delete rule");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div data-testid="governance-page">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Governance</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Governance rules</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Set thresholds per connected system and get alerted when a limit is breached.
          </p>
        </div>
        <Button onClick={openNew} disabled={!canWrite} className="gap-2 font-semibold" data-testid="add-rule-button">
          <Plus className="h-4 w-4" /> New rule
        </Button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : connected.length === 0 ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
          data-testid="rules-empty"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Cloud className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg font-bold">Connect a system first</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            A rule watches a live org. Connect Salesforce, Zoho or Oracle — then I'll help you pick a sensible first threshold.
          </p>
          <Button asChild className="mt-5 font-semibold" data-testid="rules-empty-connect">
            <Link to="/dashboard/connections">Connect a system</Link>
          </Button>
        </div>
      ) : rules.length === 0 ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
          data-testid="rules-empty"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg font-bold">No rules yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create your first governance rule to start monitoring thresholds.
          </p>
          <Button onClick={openNew} disabled={!canWrite} className="mt-5 gap-2 font-semibold">
            <Plus className="h-4 w-4" /> New rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rules-list">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 transition-colors duration-200 hover:border-primary/30 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`rule-item-${rule.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                    rule.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {rule.enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
                </div>
                <div>
                  <p className="font-display text-base font-bold tracking-tight">
                    {rule.name || rule.system_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="text-foreground">{rule.system_name}</span> · Alert when{" "}
                    <span className="font-medium text-foreground">{rule.metric_name}</span> is {rule.direction}{" "}
                    <span className="font-medium text-foreground">
                      {rule.threshold} {rule.unit}
                    </span>
                  </p>
                  <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {CHANNEL_LABEL[rule.alert_channel] || "In-app"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={() => toggleEnabled(rule)}
                    disabled={!canWrite}
                    data-testid={`rule-toggle-${rule.id}`}
                  />
                  <span className="text-xs text-muted-foreground">{rule.enabled ? "Active" : "Off"}</span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => openEdit(rule)}
                  disabled={!canWrite}
                  data-testid={`rule-edit-${rule.id}`}
                  aria-label="Edit rule"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 text-destructive hover:text-destructive"
                  onClick={() => setDeleteId(rule.id)}
                  disabled={!canWrite}
                  data-testid={`rule-delete-${rule.id}`}
                  aria-label="Delete rule"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit rule" : "New governance rule"}</DialogTitle>
            <DialogDescription>
              Choose a connected system and a metric, then set the threshold that triggers an alert.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule name</Label>
              <Input
                id="rule-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Salesforce API guardrail"
                className="h-11"
                data-testid="rule-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Connected system</Label>
              <Select value={form.system} onValueChange={(v) => setForm((f) => ({ ...f, system: v }))}>
                <SelectTrigger data-testid="rule-system-select">
                  <SelectValue placeholder="Select a system" />
                </SelectTrigger>
                <SelectContent>
                  {(connected.length ? meta.systems.filter((s) => connected.includes(s.id)) : meta.systems).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`rule-system-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Metric</Label>
              <Select value={form.metric} onValueChange={(v) => setForm((f) => ({ ...f, metric: v }))}>
                <SelectTrigger data-testid="rule-metric-select">
                  <SelectValue placeholder="Select a metric" />
                </SelectTrigger>
                <SelectContent>
                  {meta.metrics.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`rule-metric-option-${m.id}`}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="threshold">Threshold</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="threshold"
                  type="number"
                  value={form.threshold}
                  onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
                  placeholder="e.g. 80"
                  className="h-11"
                  data-testid="rule-threshold-input"
                />
                {metricUnit && <span className="whitespace-nowrap text-sm text-muted-foreground">{metricUnit}</span>}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Alert channel</Label>
              <Select
                value={form.alert_channel}
                onValueChange={(v) => setForm((f) => ({ ...f, alert_channel: v }))}
              >
                <SelectTrigger data-testid="rule-channel-select">
                  <SelectValue placeholder="Where to send alerts" />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`rule-channel-option-${c.id}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Breaches show on Overview immediately. Email and Slack routing is not available yet.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Rule active</p>
                <p className="text-xs text-muted-foreground">Turn off to pause without deleting.</p>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                data-testid="rule-enabled-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="rule-cancel">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="font-semibold" data-testid="rule-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the governance rule. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
